// Dash0 Web SDK wiring for browser errors, web vitals, page views and requests.
//
// This module has to be evaluated before anything it instruments (fetch, XHR,
// window error handlers), so it is the first import of src/main.tsx.
//
// It is a no-op unless both the Dash0 OTLP endpoint and browser ingest token are
// configured, or when VITE_DASH0_DISABLED is true. The browser token is bundled
// into the app by design; it must be a dedicated token with Ingesting permission
// for only the frontend dataset.

import {
    addSignalAttribute,
    identify,
    init,
    removeSignalAttribute,
    terminateSession,
} from "@dash0/sdk-web";
import type { UrlAttributeRecord } from "@dash0/sdk-web";
import { matchPath } from "react-router";
import {
    API_BASE_URL,
    AUTH_BASE_URL,
    DASH0_AUTH_TOKEN,
    DASH0_DATASET,
    DASH0_DISABLED,
    DASH0_ENVIRONMENT,
    DASH0_OTLP_ENDPOINT,
    DASH0_SERVICE_NAME,
    DASH0_SERVICE_VERSION,
    DASH0_SESSION_SAMPLING_RATE,
} from "@/env";

type TelemetryUser = {
    id: string;
    organisationId?: string | null;
};

const ORGANISATION_ATTRIBUTE = "onevault.organisation.id";

// Keep high-cardinality identifiers and public share tokens out of URL attributes.
// Order matters where one route is a prefix of another.
const ROUTE_PATTERNS = [
    "/settings/watermarks/identify",
    "/settings/watermarks",
    "/settings/billing",
    "/files/:fileId",
    "/files",
    "/directories/:id",
    "/directories",
    "/plans/:productId",
    "/plans",
    "/signup/:productId",
    "/signup",
    "/shared/files/:token",
    "/shared/revisions/:token",
    "/shared/directories/:token",
    "/accept-invitation",
    "/create-organisation",
    "/select-organisation",
    "/forgot-password",
    "/reset-password",
    "/billing/return",
    "/uploads",
    "/search",
    "/jobs",
    "/trash",
    "/settings",
    "/login",
    "/",
] as const;

const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|$)/giu;

function routePattern(pathname: string) {
    return ROUTE_PATTERNS.find((pattern) => matchPath({ path: pattern, end: true }, pathname))
        ?? pathname.replace(UUID_SEGMENT, "/:id");
}

/**
 * Removes query strings/fragments and replaces route identifiers before Dash0
 * applies URL attributes to page views, errors and request events.
 */
function scrubUrlAttributes(attributes: UrlAttributeRecord): UrlAttributeRecord {
    const pathname = attributes["url.path"];

    try {
        const url = new URL(attributes["url.full"]);
        url.pathname = pathname ? routePattern(pathname) : url.pathname;
        url.search = "";
        url.hash = "";

        return {
            ...attributes,
            "url.full": url.toString(),
            "url.path": url.pathname,
            "url.query": undefined,
            "url.fragment": undefined,
        };
    } catch {
        return {
            ...attributes,
            "url.path": pathname ? routePattern(pathname) : pathname,
            "url.query": undefined,
            "url.fragment": undefined,
        };
    }
}

function escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * traceparent is only propagated to the origins we own. Sending it elsewhere adds
 * an unexpected header to CORS preflight requests, which third parties can reject.
 */
function tracePropagationTargets() {
    const origins = new Set<string>();

    for (const baseUrl of [API_BASE_URL, AUTH_BASE_URL]) {
        try {
            origins.add(new URL(baseUrl).origin);
        } catch {
            // A malformed base URL already breaks the app elsewhere; don't let it stop telemetry.
        }
    }

    return [...origins].map((origin) => new RegExp(`^${escapeRegExp(origin)}(?:/|$)`, "u"));
}

export const telemetryEnabled = Boolean(
    DASH0_OTLP_ENDPOINT && DASH0_AUTH_TOKEN && !DASH0_DISABLED,
);

if (telemetryEnabled) {
    init({
        serviceName: DASH0_SERVICE_NAME,
        serviceVersion: DASH0_SERVICE_VERSION,
        environment: DASH0_ENVIRONMENT,
        deploymentId: DASH0_SERVICE_VERSION,
        endpoint: {
            url: DASH0_OTLP_ENDPOINT,
            authToken: DASH0_AUTH_TOKEN,
            ...(DASH0_DATASET ? { dataset: DASH0_DATASET } : {}),
        },
        pageViewInstrumentation: {
            generateMetadata: (url) => ({ title: routePattern(url.pathname) }),
        },
        propagators: [
            {
                type: "traceparent",
                match: tracePropagationTargets(),
            },
        ],
        sessionSamplingRate: DASH0_SESSION_SAMPLING_RATE,
        urlAttributeScrubber: scrubUrlAttributes,
    });
}

// Auth guards run on every navigation, so only update Dash0 when the user or
// active organisation actually changes.
let currentUserKey: string | undefined;

/** Attaches only stable identifiers to subsequent signals; no email or name is sent. */
export function setTelemetryUser(user: TelemetryUser) {
    const key = `${user.id}|${user.organisationId ?? ""}`;

    if (!telemetryEnabled || key === currentUserKey) {
        return;
    }

    currentUserKey = key;
    identify(user.id);

    if (user.organisationId) {
        addSignalAttribute(ORGANISATION_ATTRIBUTE, user.organisationId);
    } else {
        removeSignalAttribute(ORGANISATION_ATTRIBUTE);
    }
}

/** Clears user context and makes the next physical page load start a new session. */
export function clearTelemetryUser() {
    currentUserKey = undefined;

    if (!telemetryEnabled) {
        return;
    }

    identify();
    removeSignalAttribute(ORGANISATION_ATTRIBUTE);
    terminateSession();
}
